import React from "react";
import { isMobile, isTablet } from "react-device-detect";
import { SavedWidgetLayout } from "@lifeat/shared/data-models/widget/WidgetDataModels";
import { AmplitudeMissingResizeObserverEvent } from "@analytics/Amplitude/AmplitudeEngineeringInsightEvent";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import { ToDesktopUtil } from "@desktop/index";
import ResizeIcon from "@icons/DragResizeIcon.svg";
import MinusIcon from "@icons/MinusIcon.svg";
import { logWarn } from "@service/ConsoleLoggerService";
import Styles from "@themes/Styles";
import WidgetCanvasContext from "../../WidgetCanvasContext";
import { WIDGETS_STARTING_Z_IDX } from "../Workspace";
import "./Widget.scss";

// The buffer/offset distance we set for what valid position a widget can have
// relative to the canvas (e.g. 40px inwards from all sides of the canvas).
const WIDGET_POSITION_EDGE_OFFSET_X = 10;
// Y-offset is set to 48px, because top navigation height is 40. (16px for the margin above the sidebar)
const WIDGET_POSITION_EDGE_OFFSET_Y = 56;

// Defaults determined for ideal widget sizing
const DEFAULT_HEIGHT = "auto";
const DEFAULT_WIDTH = 360;
const DEFAULT_MIN_HEIGHT = 150;
const DEFAULT_MIN_WIDTH = DEFAULT_WIDTH;

interface PosType {
    readonly x: number;
    readonly y: number;
}

interface WidgetProps {
    /**
     * The widget name (defined in {@link WidgetDataManager.ts}
     */
    readonly name: string;
    /**
     * Tell amplitude where the event is triggered from.
     */
    readonly amplitudeEventSource: AmplitudeEventSource;
    /**
     * If the user is logged in.
     * TODO: this shouldn't be a prop. It should use UserContext
     */
    readonly isUserAuthenticated: boolean;
    /**
     * TODO: should not be a prop. Should use CallToActionContext
     */
    readonly unauthenticatedUserCallback?: () => void;
    /**
     * If the widget name should be shown in the header.
     */
    readonly showTitle: boolean;
    /**
     * Optional, something custom to put in the widget header.
     */
    readonly headerContent?: React.ReactNode;
    /**
     * Optional, something custom to put in the widget footer.
     */
    readonly footerContent?: React.ReactNode;
    /**
     * If the widget is open on the workspace canvas.
     */
    readonly isOpen: boolean;
    /**
     * If the widget has a fixed position on the UI and cannot be closed
     */
    readonly isStatic: boolean;
    /**
     * If the widget cannot be moved around the canvas.
     */
    readonly isPositionFixed?: boolean;
    /**
     * If the widget can be resized using the bottom right corner.
     */
    readonly isResizable?: boolean;
    /**
     * If the widget footer auto hides. Defaults to true.
     */
    readonly footerAutoHide: boolean;
    /**
     * The initial position of the widget on the canvas/screen.
     */
    readonly initialPos?: PosType;
    /**
     * Action to perform to open the widget.
     */
    readonly openWidget?: () => void;
    /**
     * Action to perform when closing the widget (by clicking the close button on the widget).
     */
    readonly closeWidget?: () => void;
    /**
     * Styling light vs dark
     * TODO: remove this once we convert to functional component.
     */
    readonly lightMode: boolean;
    /**
     * Style the widget in transparency mode if present (ignores light/dark).
     */
    readonly transparent?: boolean;
    /**
     * The last saved position of the widget.
     */
    readonly savedLayout?: SavedWidgetLayout;
    /**
     * Action to perform in order to persist the position of the widget on the canvas.
     */
    readonly updateSavedLayout?: (updatedLayout: SavedWidgetLayout) => void;
    /**
     * Optional, forces a minimum width for this element
     */
    readonly minWidth?: number;
    /**
     * Optional, forces a minimum height for this element
     */
    readonly minHeight?: number;
    /**
     * Force override the height of the widget from the default of 'auto'.
     * This still works for widgets that are resizable and those that are not.
     */
    readonly overrideHeight?: number;
    /**
     * Force override the width of the widget from the default of 360px.
     * NOTE: Only use if widget is not resizable.
     */
    readonly overrideWidth?: number;
    readonly children?: React.ReactNode;
    readonly className?: string;
}

interface WidgetState {
    currentPos: PosType;
    dragging: boolean;
    relativePos: PosType;
    zIdx: number;
    dimensions: PosType; // x = width, y = height
    resizing: boolean;
    resizeDiff: PosType;
    hideFooter: boolean;
}

class Widget extends React.Component<WidgetProps, WidgetState> {
    amplitudeService: AmplitudeService;
    resizeObserver?: ResizeObserver;

    static override contextType = WidgetCanvasContext;

    // For maintaining a reference to the DOM element for the whole widget.
    private widgetDiv: HTMLDivElement;

    constructor(props: WidgetProps) {
        super(props);

        // The initial position is either the saved position from the last visit, OR
        // the default position of the widget (set in Workspace.tsx).
        const initPos =
            props.savedLayout != null && props.savedLayout.xPos && props.savedLayout.yPos
                ? { x: props.savedLayout.xPos, y: props.savedLayout.yPos }
                : props.initialPos;

        const initDimensions =
            props.savedLayout != null && props.savedLayout.height && props.savedLayout.width
                ? { x: props.savedLayout.width, y: props.savedLayout.height }
                : undefined; // if defaulted, it will be set in the style attribute for the widget

        this.state = {
            currentPos: initPos,
            dragging: false,
            relativePos: null,
            zIdx: WIDGETS_STARTING_Z_IDX,
            dimensions: initDimensions,
            resizing: false,
            resizeDiff: { x: 0, y: 0 },
            hideFooter: !isMobile && !isTablet && this.props.footerAutoHide && true,
        };

        this.amplitudeService = AmplitudeService.getInstance();

        this.handleZIdxUpdate = this.handleZIdxUpdate.bind(this);
        this.keepWidgetInWindow = this.keepWidgetInWindow.bind(this);

        this.setBoundedDraggingState = this.setBoundedDraggingState.bind(this);
        this.handleUserStopDrag = this.handleUserStopDrag.bind(this);

        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);

        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);

        this.onResizeStart = this.onResizeStart.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onResizeStop = this.onResizeStop.bind(this);

        this.onTouchResizeStart = this.onTouchResizeStart.bind(this);
        this.onTouchResize = this.onTouchResize.bind(this);
        this.onTouchResizeStop = this.onTouchResizeStop.bind(this);

        this.closeWidgetWrapper = this.closeWidgetWrapper.bind(this);
    }

    override componentDidMount() {
        if (!ToDesktopUtil.isWindowMenubar()) {
            if (
                this.state.currentPos.x < WIDGET_POSITION_EDGE_OFFSET_X ||
                this.state.currentPos.y < WIDGET_POSITION_EDGE_OFFSET_Y
            ) {
                this.setState({
                    currentPos: {
                        x: WIDGET_POSITION_EDGE_OFFSET_X,
                        y: WIDGET_POSITION_EDGE_OFFSET_Y,
                    },
                });
            }
            if (window.ResizeObserver == null) {
                logWarn("This browser does not support ResizeObserver API.");
                this.amplitudeService.logEvent(new AmplitudeMissingResizeObserverEvent());
            } else {
                this.resizeObserver = new window.ResizeObserver(this.keepWidgetInWindow);
                this.resizeObserver.observe(this.widgetDiv);
            }
            // Listen for window resizing to adjust the widget positions that go off screen.
            window.addEventListener("resize", this.keepWidgetInWindow);
        }
    }

    override componentDidUpdate(props: WidgetProps, state: WidgetState): void {
        if (this.state.dragging && !state.dragging) {
            document.addEventListener("mousemove", this.onMouseMove);
            document.addEventListener("mouseup", this.onMouseUp);

            document.addEventListener("touchmove", this.onTouchMove);
            document.addEventListener("touchend", this.onTouchEnd);
        } else if (!this.state.dragging && state.dragging) {
            document.removeEventListener("mousemove", this.onMouseMove);
            document.removeEventListener("mouseup", this.onMouseUp);

            document.removeEventListener("touchmove", this.onTouchMove);
            document.removeEventListener("touchend", this.onTouchEnd);
        } else if (this.state.resizing && !state.resizing) {
            document.addEventListener("mousemove", this.onResize);
            document.addEventListener("mouseup", this.onResizeStop);

            document.addEventListener("touchmove", this.onTouchResize);
            document.addEventListener("touchend", this.onTouchResizeStop);
        } else if (!this.state.resizing && state.resizing) {
            document.removeEventListener("mousemove", this.onResize);
            document.removeEventListener("mouseup", this.onResizeStop);

            document.removeEventListener("touchmove", this.onTouchResize);
            document.removeEventListener("touchend", this.onTouchResizeStop);
        }
    }

    override componentWillUnmount() {
        // Remove all event listeners
        this.resizeObserver?.disconnect();
        document.removeEventListener("mousemove", this.onMouseMove);
        document.removeEventListener("mouseup", this.onMouseUp);

        document.removeEventListener("touchmove", this.onTouchMove);
        document.removeEventListener("touchend", this.onTouchEnd);

        document.removeEventListener("mousemove", this.onResize);
        document.removeEventListener("mouseup", this.onResizeStop);

        window.removeEventListener("resize", this.keepWidgetInWindow);
    }

    handleZIdxUpdate(): void {
        const { activeWidgetZIndex, setActiveWidgetZIndex } = this.context;
        // If this widget isn't already the widget with the highest z-index, update it.
        if (activeWidgetZIndex - this.state.zIdx !== 0) {
            setActiveWidgetZIndex(activeWidgetZIndex + 1);
            this.setState({ zIdx: activeWidgetZIndex + 1 });
        }
    }

    // Helper to update state and the saved widget layout info.
    handleUserStopDrag(): void {
        this.setState({ dragging: false });

        if (this.props.updateSavedLayout) {
            this.props.updateSavedLayout({
                ...this.props.savedLayout,
                xPos: this.state.currentPos.x,
                yPos: this.state.currentPos.y,
            });
        }
    }

    /**
     * Update position state of widget when dragged by user, with the final
     * position set being bounded by the widget canvas size.
     * @param interactX The X-coordinate of the widget as it is moved by the user (click or touch).
     * @param interactY The Y-coordinate of the widget as it is moved by the user (click or touch).
     */
    setBoundedDraggingState(interactX: number, interactY: number): void {
        // Using constants to make the code more readable.
        const xPosMin = 0;
        const yPosMin = WIDGET_POSITION_EDGE_OFFSET_Y;
        // user can drag widget all the way to bottom and far right of screen
        const xPosMax = window.innerWidth - this.state.dimensions.x;
        const yPosMax = window.innerHeight - this.state.dimensions.y;

        // Determine the possible min/max X and Y positions based on the current
        // dragging position.
        const xPosClicked = interactX - this.state.relativePos.x;
        const xPos = Math.min(Math.max(xPosMin, xPosClicked), xPosMax);

        const yPosClicked = interactY - this.state.relativePos.y;
        const yPos = Math.min(Math.max(yPosMin, yPosClicked), yPosMax);

        this.setState({
            currentPos: {
                x: xPos,
                y: yPos,
            },
        });
    }

    /**
     * Update a widget's position on the canvas if the browser window is resized,
     * so the widget's stay on screen and don't disappear.
     */
    keepWidgetInWindow(): void {
        // Only enter this if we have a ref for the widget component wrapper, or if the
        // dimensions have been set in state.
        if (
            (this.widgetDiv &&
                (this.state.currentPos.x + this.widgetDiv.clientWidth > window.innerWidth ||
                    this.state.currentPos.y + this.widgetDiv.clientHeight > window.innerHeight)) ||
            (this.state.dimensions &&
                (this.state.currentPos.x + this.state.dimensions.x > window.innerWidth ||
                    this.state.currentPos.y + this.state.dimensions.y > window.innerHeight))
        ) {
            const widthVal = this.state.dimensions ? this.state.dimensions.x : this.widgetDiv.clientWidth;
            const heightVal = this.state.dimensions ? this.state.dimensions.y : this.widgetDiv.clientHeight;
            this.setState({
                currentPos: {
                    x: Math.min(this.state.currentPos.x, window.innerWidth - widthVal),
                    y: Math.min(this.state.currentPos.y, window.innerHeight - heightVal),
                },
            });

            // Updates the saved widget layout state for when it gets saved to localstorage.
            this.handleUserStopDrag();
        } else if (this.state.currentPos.x < 0 || this.state.currentPos.y < WIDGET_POSITION_EDGE_OFFSET_Y) {
            this.setState({
                currentPos: {
                    x: Math.max(this.state.currentPos.x, 0),
                    y: Math.max(this.state.currentPos.y, WIDGET_POSITION_EDGE_OFFSET_Y), // 50 is to account for the top navigation bar height
                },
            });

            // Updates the saved widget layout state for when it gets saved to localstorage.
            this.handleUserStopDrag();
        }
    }

    setStateOnWidgetMoveStart(x: number, y: number, e: React.MouseEvent | React.TouchEvent): void {
        this.setState({
            dragging: true,
            relativePos: {
                x: x - this.state.currentPos.x,
                y: y - this.state.currentPos.y,
            },
            // We continuously check and set the dimensions because widget dimensions
            // may change based on the user's interaction (e.g. to-do list task elements
            // will increase the widget height).
            // Use 2 parentElement references to get the dimension because the
            // onMouseDown/onTouchStart happens with the widget header content (see returned JSX)
            dimensions: {
                x: e.currentTarget.parentElement.getBoundingClientRect().width,
                y: e.currentTarget.parentElement.getBoundingClientRect().height,
            },
        });
    }

    // User clicks down beginning of drag/interaction event
    onMouseDown(e: React.MouseEvent): void {
        if (!this.props.isStatic && !this.props.isPositionFixed) {
            if (e.button !== 0) {
                return;
            }

            // Update z-index here because the widget header div's onMouseDown
            // takes precedence over the overall widget div's onMouseDown
            this.handleZIdxUpdate();

            this.setStateOnWidgetMoveStart(e.pageX, e.pageY, e);

            e.stopPropagation();
            e.preventDefault();
        }
    }

    // User actively dragging
    onMouseMove(e: MouseEvent): void {
        if (!this.state.dragging) {
            return;
        }

        this.setBoundedDraggingState(e.pageX, e.pageY);

        e.stopPropagation();
        e.preventDefault();
    }

    // User stops dragging
    onMouseUp(e: MouseEvent): void {
        this.handleUserStopDrag();

        e.stopPropagation();
        e.preventDefault();
    }

    // User starts dragging (touch devices)
    onTouchStart(e: React.TouchEvent<HTMLDivElement>): void {
        if (!this.props.isStatic && !this.props.isPositionFixed) {
            this.handleZIdxUpdate();

            // E.g. if user is scrolling dropdown menu in widget header, we don't
            // want to move the widget around.
            if (e.target !== e.currentTarget) {
                e.stopPropagation();
                e.preventDefault();
                return;
            }

            const touch = e.targetTouches[0];
            this.setStateOnWidgetMoveStart(touch.clientX, touch.clientY, e);

            e.stopPropagation();
        }
    }

    // User actively dragging (touch devices)
    onTouchMove(e: TouchEvent): any {
        if (!this.state.dragging) {
            return;
        }

        const touch = e.targetTouches[0];

        this.setBoundedDraggingState(touch.clientX, touch.clientY);

        e.stopPropagation();
    }

    closeWidgetWrapper() {
        // Log close events (note: we do not log for Amplitude)
        GA4Service.logEvent(GA4Event.TOGGLE_WIDGET, {
            [GA4EventProperties.widget_name]: this.props.name,
            [GA4EventProperties.toggle_widget.key]: GA4EventProperties.toggle_widget.closed,
        });

        this.props.closeWidget();
    }

    // User stops dragging (touch devices)
    onTouchEnd(e: TouchEvent): any {
        this.handleUserStopDrag();

        e.stopPropagation();
    }

    calculateResizeDimensions(clientX: number, clientY: number): PosType {
        const width = this.state.dimensions.x;
        const height = this.state.dimensions.y;

        const { x, y } = this.state.resizeDiff;
        const xDiff = Math.abs(x - clientX);
        const yDiff = Math.abs(y - clientY);

        // Allows drag to the right to increase width
        const newW = x > clientX ? width - xDiff : width + xDiff;
        // Allows drag downwards to increase height
        const newH = y < clientY ? height + yDiff : height - yDiff;

        return { x: newW, y: newH };
    }

    setDimensionsOnResize(e: React.MouseEvent | React.TouchEvent) {
        // If the dimensions are not set (i.e. defaulted to the constants at the top
        // of this file), then set them here.
        if (!this.state.dimensions) {
            this.setState({
                dimensions: {
                    x: e.currentTarget.parentElement.parentElement.getBoundingClientRect().width,
                    y: e.currentTarget.parentElement.parentElement.getBoundingClientRect().height,
                },
            });
        }
    }

    onResizeStart(e: React.MouseEvent): void {
        if (!this.state.dragging && this.props.isResizable) {
            e.preventDefault();
            this.setState({
                resizing: true,
                resizeDiff: { x: e.clientX, y: e.clientY },
            });

            this.setDimensionsOnResize(e);
        }
    }

    // Source: https://stackoverflow.com/a/60794375
    onResize(e: MouseEvent): void {
        if (this.props.isResizable && this.state.resizing && !this.state.dragging) {
            e.preventDefault();

            const newDims = this.calculateResizeDimensions(e.clientX, e.clientY);

            this.setState({
                resizeDiff: { x: e.clientX, y: e.clientY },
                dimensions: { x: newDims.x, y: newDims.y },
            });
        }
    }

    onResizeStop(e: MouseEvent | TouchEvent): void {
        if (this.props.isResizable && this.state.resizing && !this.state.dragging) {
            e.preventDefault();
            this.setState({ resizing: false });

            // Save the widget size
            if (this.props.updateSavedLayout) {
                this.props.updateSavedLayout({
                    ...this.props.savedLayout,
                    width: this.state.dimensions.x,
                    height: this.state.dimensions.y,
                });
            }
        }
    }

    onTouchResizeStart(e: React.TouchEvent): void {
        if (!this.state.dragging && this.props.isResizable) {
            e.preventDefault();

            const touch = e.targetTouches[0];

            this.setState({
                resizing: true,
                resizeDiff: { x: touch.clientX, y: touch.clientY },
            });

            this.setDimensionsOnResize(e);
        }
    }

    onTouchResize(e: TouchEvent): void {
        if (this.props.isResizable && this.state.resizing && !this.state.dragging) {
            e.preventDefault();

            const touch = e.targetTouches[0];

            const newDims = this.calculateResizeDimensions(touch.clientX, touch.clientY);

            this.setState({
                resizeDiff: { x: touch.clientX, y: touch.clientY },
                dimensions: { x: newDims.x, y: newDims.y },
            });
        }
    }

    onTouchResizeStop(e: TouchEvent): void {
        this.onResizeStop(e);
    }

    override render() {
        const widgetContent = this.props.children;

        const widgetHeaderContent = this.props.headerContent ? this.props.headerContent : this.props.name;

        const widgetStyle = {
            display: this.props.isOpen ? "flex" : "none", // Widget containing div is flexbox
            zIndex: this.state.zIdx,
            left: this.state.currentPos.x + "px",
            top: this.state.currentPos.y + "px",
            width:
                this.state.dimensions && this.props.isResizable
                    ? this.state.dimensions.x + "px"
                    : this.props.overrideWidth ?? DEFAULT_WIDTH,
            height:
                this.state.dimensions && this.props.isResizable
                    ? this.state.dimensions.y + "px"
                    : this.props.overrideHeight ?? DEFAULT_HEIGHT,
            minWidth: this.props.minWidth ? this.props.minWidth + "px" : DEFAULT_MIN_WIDTH,
            minHeight: this.props.minHeight ? this.props.minHeight + "px" : DEFAULT_MIN_HEIGHT,
            maxHeight: `calc(${window.innerHeight}px - ${Styles.topBarHeight}`,
            maxWidth: `calc(${window.innerWidth}px - ${Styles.topBarHeight}`,
        };

        const getWidgetStyleClassName = () => {
            const lightModeClass = this.props.lightMode ? " light" : " dark";
            return this.props.transparent ? " transparent" : lightModeClass;
        };

        return (
            <div
                className={`widget ${this.props.className} ${getWidgetStyleClassName()}`}
                style={ToDesktopUtil.isWindowMenubar() ? undefined : widgetStyle}
                onMouseDown={this.handleZIdxUpdate}
                ref={(widgetDiv) => {
                    this.widgetDiv = widgetDiv;
                }}
                onMouseEnter={() => this.setState({ hideFooter: false })}
                onMouseLeave={() => {
                    if (this.props.footerAutoHide) {
                        this.setState({ hideFooter: !isMobile && !isTablet && true });
                    }
                }}
            >
                <div
                    className={`widget-header ${getWidgetStyleClassName()}`}
                    onMouseDown={this.onMouseDown}
                    onTouchStart={this.onTouchStart}
                >
                    <div className={"widget-header-content"}>{widgetHeaderContent}</div>

                    {this.props.closeWidget && (
                        <span className={"close-widget-button"}>
                            <LifeAtButton
                                icon={<MinusIcon className={`minus-icon ${this.props.lightMode ? "light" : "dark"}`} />}
                                isLink={false}
                                handleClick={this.closeWidgetWrapper}
                            />
                        </span>
                    )}
                </div>

                <div className={`widget-content`}>
                    {!this.props.isUserAuthenticated && (
                        <div
                            style={{
                                position: "absolute",
                                height: "100%",
                                width: "100%",
                                background: "black",
                                opacity: 0,
                            }}
                            onClick={(e) => {
                                if (!this.props.isUserAuthenticated) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.props.unauthenticatedUserCallback();
                                }
                            }}
                        />
                    )}
                    {widgetContent}
                </div>

                <div
                    className={`widget-footer ${this.state.hideFooter ? "hidden" : ""} ${
                        this.props.footerContent ? "" : "float-right"
                    }`}
                >
                    {this.props.footerContent}

                    {this.props.isResizable && (
                        <ResizeIcon
                            className={`resize-widget-corner ${this.props.lightMode ? "light" : "dark"}`}
                            onMouseDown={this.onResizeStart}
                            onTouchStart={this.onTouchResizeStart}
                        />
                    )}
                </div>
            </div>
        );
    }
    static defaultProps = {
        isStatic: false,
        initialPos: { x: window.innerWidth / 2, y: 100 },
        footerAutoHide: true,
    };
}
export { Widget as default, type WidgetProps };
